/* eslint-disable no-console */
import { getPartner } from './partner'
import { getUser } from './user'
const { version } = require('../../package.json')

// Store console logs in memory for support purposes
let consoleLogHistory = []
const MAX_LOG_ENTRIES = 100
const MAX_REPORT_ERRORS = 8
const MAX_MESSAGE_LENGTH = 600
const IS_PRODUCTION = process.env.NODE_ENV === 'production'

// Letzte Routen, damit der Report zeigt, wo der Fehler passiert ist.
// window.location steht beim Absenden immer auf /support.
const MAX_ROUTE_HISTORY = 5
const SUPPORT_PATH = '/support'
let routeHistory = []

// Override console methods to capture logs
const originalConsoleLog = console.log
const originalConsoleWarn = console.warn

// Safe JSON stringify that handles circular references
const safeStringify = (obj, space = 2) => {
  const seen = new WeakSet()
  return JSON.stringify(
    obj,
    (key, val) => {
      if (val != null && typeof val === 'object') {
        if (seen.has(val)) {
          return '[Circular Reference]'
        }
        seen.add(val)
      }
      return val
    },
    space
  )
}

// Tokens, Mailadressen und Passwoerter duerfen nicht im Ticket landen.
export const redactSensitive = (text) =>
  String(text)
    .replace(/\bBearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [redacted]')
    .replace(
      /eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g,
      '[jwt]'
    )
    .replace(
      /("?(?:password|passwort|token|idToken|accessToken|refreshToken|secret|apiKey)"?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,}]+)/gi,
      '$1"[redacted]"'
    )
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')

const addToLogHistory = (level, args) => {
  // In Production zaehlen nur Fehler. Alles andere ist Rauschen im Ticket.
  if (IS_PRODUCTION && level !== 'error') return

  const timestamp = new Date().toISOString()
  const message = args
    .map((arg) => {
      if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}\n${arg.stack || ''}`
      }
      if (typeof arg === 'object' && arg !== null) {
        try {
          return safeStringify(arg, 2)
        } catch (error) {
          return `[Object: ${arg.constructor?.name || 'Unknown'} - stringify failed: ${error.message}]`
        }
      }
      return String(arg)
    })
    .join(' ')

  consoleLogHistory.push({
    timestamp,
    level,
    message: redactSensitive(message).substring(0, 1000) // Limit message length
  })

  // Keep only the last MAX_LOG_ENTRIES
  if (consoleLogHistory.length > MAX_LOG_ENTRIES) {
    consoleLogHistory = consoleLogHistory.slice(-MAX_LOG_ENTRIES)
  }
}

// Override console methods
console.log = (...args) => {
  originalConsoleLog.apply(console, args)
  addToLogHistory('log', args)
}

console.warn = (...args) => {
  originalConsoleWarn.apply(console, args)
  addToLogHistory('warn', args)
}

// Hook into console.error - note that it might already be overridden by Bugsnag
// We'll add our logging to any existing override
const currentConsoleError = console.error
console.error = (...args) => {
  // Call the current console.error (which might be Bugsnag's override)
  currentConsoleError.apply(console, args)
  // Add to our log history
  addToLogHistory('error', args)
}

// Also capture unhandled errors and promise rejections
window.addEventListener('error', (event) => {
  addToLogHistory('error', [
    `Unhandled Error: ${event.message}`,
    `File: ${event.filename}:${event.lineno}:${event.colno}`,
    event.error?.stack || ''
  ])
})

window.addEventListener('unhandledrejection', (event) => {
  const { reason } = event
  addToLogHistory('error', [
    `Unhandled Promise Rejection: ${reason?.message || reason}`,
    reason?.stack || ''
  ])
})

// Route tracking ueber die History-API (react-router nutzt pushState)
const recordRoute = () => {
  const path = window.location.pathname
  if (path === SUPPORT_PATH || routeHistory[routeHistory.length - 1] === path) {
    return
  }
  routeHistory = [...routeHistory, path].slice(-MAX_ROUTE_HISTORY)
}

recordRoute()
;['pushState', 'replaceState'].forEach((method) => {
  const original = window.history[method]
  window.history[method] = function (...args) {
    const result = original.apply(this, args)
    recordRoute()
    return result
  }
})
window.addEventListener('popstate', recordRoute)

// Gleiche Fehler zusammenfassen, damit eine Schleife nicht 50 Zeilen fuellt
const groupErrors = (entries) => {
  const groups = new Map()
  entries.forEach((entry) => {
    const key = entry.message.substring(0, 200)
    const group = groups.get(key)
    if (group) {
      group.count += 1
      group.lastTimestamp = entry.timestamp
    } else {
      groups.set(key, { ...entry, count: 1, lastTimestamp: entry.timestamp })
    }
  })
  return [...groups.values()]
}

/**
 * Collect recent browser logs and system information for support
 * @param {number} maxEntries - Maximum number of log entries to include
 * @returns {string} Formatted log information
 */
export const collectLogsForSupport = (maxEntries = MAX_REPORT_ERRORS) => {
  const user = getUser()
  const partner = getPartner()

  const systemInfo = {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    previousRoute: routeHistory[routeHistory.length - 1] || 'unknown',
    routeHistory: routeHistory.join(' > ') || 'unknown',
    dashboardVersion: version,
    userId: user?.username || 'unknown',
    partnerId: partner?.id || 'unknown',
    partnerName: partner?.name || 'unknown',
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    onlineStatus: navigator.onLine
  }

  const errors = groupErrors(
    consoleLogHistory.filter((log) => log.level === 'error')
  ).slice(-maxEntries)

  let logReport = '=== SYSTEM INFORMATION ===\n'
  Object.entries(systemInfo).forEach(([key, value]) => {
    logReport += `${key}: ${value}\n`
  })

  logReport += `\n=== RECENT ERRORS (${errors.length}) ===\n`
  if (errors.length === 0) {
    logReport += 'No errors recorded\n'
  } else {
    errors.forEach((log) => {
      const count = log.count > 1 ? ` x${log.count}` : ''
      logReport += `[${log.lastTimestamp}]${count} ${log.message.substring(0, MAX_MESSAGE_LENGTH)}\n`
    })
  }

  return logReport
}

/**
 * Create a mailto link with logs included
 * @param {string} subject - Email subject
 * @param {string} body - Additional email body content
 * @returns {string} Mailto URL
 */
export const createSupportEmailWithLogs = (
  subject = 'Dashboard Support Request',
  body = ''
) => {
  const logs = collectLogsForSupport()
  const emailBody = `${body}\n\n--- DEBUG INFORMATION ---\n${logs}`

  const params = new URLSearchParams({
    subject,
    body: emailBody
  })

  return `mailto:help@aivy.app?${params.toString()}`
}

/**
 * Get recent error logs from console history
 * @param {number} maxEntries - Maximum number of error entries
 * @returns {Array} Array of error log entries
 */
export const getRecentErrors = (maxEntries = 10) => {
  return consoleLogHistory
    .filter((log) => log.level === 'error' || log.level === 'warn')
    .slice(-maxEntries)
}

/**
 * Clear the console log history
 */
export const clearLogHistory = () => {
  consoleLogHistory = []
}
